import type { AuditEvent } from '@gestion-permisos/contracts';
import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { ADMIN_PAGE_SIZE } from '../admin/admin-api.js';
import { BackToAdmin, Field, firstForbidden, INPUT } from '../admin/admin-ui.js';
import { type AuditFilters, useAuditPage } from '../admin/audit-api.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card, CardContent } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { ProblemMessage } from '../components/problem-message.js';
import { formatInstant } from '../requests/format.js';
import { pageFrom, Pagination, PastLastPage, TABLE, TD, TH, usePageHref } from '../requests/request-list.js';

const ACTOR_LABELS: Record<AuditEvent['actorType'], string> = { USER: 'Usuario', SYSTEM: 'Sistema', JOB: 'Trabajo', MIGRATION: 'Migración' };

/** Parámetro de la URL de cada filtro. */
const PARAMS: Record<keyof AuditFilters, string> = { entityType: 'tipo', entityId: 'entidad', actorId: 'actor', from: 'desde', to: 'hasta' };
const FIELDS: [keyof AuditFilters, string, string][] = [
  ['entityType', 'Tipo de entidad', 'request'],
  ['entityId', 'Entidad (identificador)', ''],
  ['actorId', 'Actor (identificador)', ''],
  ['from', 'Desde (instante ISO)', '2030-03-01T00:00:00Z'],
  ['to', 'Hasta (instante ISO)', '2030-03-31T23:59:59Z'],
];

function filtersFrom(params: URLSearchParams): AuditFilters {
  const value = (key: keyof AuditFilters) => params.get(PARAMS[key]) ?? '';
  return { entityType: value('entityType'), entityId: value('entityId'), actorId: value('actorId'), from: value('from'), to: value('to') };
}

/**
 * Auditoría (W10), solo lectura y solo para administradores: una página de `GET /audit-events`, con la
 * página (`?pagina=`) y los filtros en la URL (`?tipo=`, `?entidad=`, `?actor=`, `?desde=`, `?hasta=`).
 * `desde` y `hasta` se envían tal como se escriben, sin convertirlos con `Date`; la API los valida (400).
 * `changes` y `metadata` se muestran tal como llegan, ya saneados por la API. Ningún identificador se
 * muestra como nombre. Con el aspecto de Mis solicitudes: cabecera, y los filtros y la tabla en tarjetas.
 */
export function AdminAuditPage() {
  const [params, setParams] = useSearchParams();
  const page = pageFrom(params, 'pagina');
  const pageHref = usePageHref('pagina');
  const filters = filtersFrom(params);
  const audit = useAuditPage(filters, page);

  const denied = firstForbidden(audit.error);
  if (denied) return <ApiErrorView error={denied} />;

  const apply = (next: AuditFilters) =>
    setParams(() => {
      const updated = new URLSearchParams();
      for (const key of Object.keys(PARAMS) as (keyof AuditFilters)[]) {
        const value = next[key].trim();
        if (value !== '') updated.set(PARAMS[key], value);
      }
      return updated;
    });

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <BackToAdmin />
      <PageHeader title="Auditoría" description="Eventos de auditoría (solo lectura)." />
      <Card>
        <CardContent>
          {/* La clave reinicia el formulario cuando cambian los filtros de la URL (p. ej., al volver atrás). */}
          <FiltersForm key={params.toString()} initial={filters} onApply={apply} />
        </CardContent>
      </Card>

      <div>
        {audit.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
        {/* Un 400 (p. ej., «desde» posterior a «hasta») se muestra una vez; los filtros siguen a la vista. */}
        {audit.isError ? <ProblemMessage error={audit.error} /> : null}
        {audit.isSuccess ? (
          // Una sola tarjeta para la tabla, el vacío o la página más allá del final.
          <Card flush>
            {audit.data.total === 0 ? <p className="px-5 py-16 text-center text-sm text-muted-foreground">No hay eventos de auditoría con estos filtros.</p> : null}
            {audit.data.total > 0 && audit.data.items.length === 0 ? (
              <div className="px-5">
                <PastLastPage firstPage={pageHref(1)} what="eventos" />
              </div>
            ) : null}
            {audit.data.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className={TABLE} aria-label="Eventos de auditoría">
                  <thead className="border-b border-border bg-muted text-muted-foreground">
                    <tr>
                      <th scope="col" className={TH}>Instante</th>
                      <th scope="col" className={TH}>Actor</th>
                      <th scope="col" className={TH}>Acción</th>
                      <th scope="col" className={TH}>Entidad</th>
                      <th scope="col" className={TH}>Cambios</th>
                      <th scope="col" className={TH}>Metadatos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.data.items.map((event) => (
                      <tr key={event.id} className="border-t border-border align-top transition-colors first:border-t-0 hover:bg-muted">
                        <th scope="row" className={TH}>
                          <time dateTime={event.occurredAt}>{formatInstant(event.occurredAt)}</time>
                        </th>
                        <td className={TD}>{ACTOR_LABELS[event.actorType]}</td>
                        <td className={TD}>{event.action}</td>
                        <td className={TD}>
                          <EntityCell event={event} />
                        </td>
                        <td className={TD}>
                          <RawJson value={event.changes} label="Cambios" />
                        </td>
                        <td className={TD}>
                          <RawJson value={event.metadata} label="Metadatos" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Card>
        ) : null}
        {audit.isSuccess && audit.data.total > 0 ? (
          <Pagination page={page} total={audit.data.total} pageSize={ADMIN_PAGE_SIZE} hrefFor={pageHref} units={['evento', 'eventos']} />
        ) : null}
      </div>
    </div>
  );
}

function FiltersForm({ initial, onApply }: { initial: AuditFilters; onApply: (filters: AuditFilters) => void }) {
  const [draft, setDraft] = useState<AuditFilters>(initial);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onApply(draft);
  };
  return (
    <form noValidate onSubmit={submit} aria-label="Filtros de auditoría" className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {FIELDS.map(([key, label, placeholder]) => (
          <Field key={key} id={`audit-${key}`} label={label}>
            <input
              id={`audit-${key}`}
              value={draft[key]}
              placeholder={placeholder}
              onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}
              className={INPUT}
            />
          </Field>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={buttonClasses()}>
          Filtrar
        </button>
        <button type="button" className={buttonClasses('outline')} onClick={() => onApply({ entityType: '', entityId: '', actorId: '', from: '', to: '' })}>
          Quitar filtros
        </button>
      </div>
    </form>
  );
}

/** Tipo de entidad; si es una solicitud con un UUID, enlace a su detalle administrativo (sin mostrar el id). */
function EntityCell({ event }: { event: AuditEvent }) {
  const isRequest = event.entityType === 'request' && event.entityId !== null && z.uuid().safeParse(event.entityId).success;
  return (
    <>
      {event.entityType}
      {isRequest ? (
        <>
          {' · '}
          <Link to={`/administracion/solicitudes/${event.entityId}`} className="text-primary underline-offset-4 hover:underline">
            Ver solicitud
          </Link>
        </>
      ) : null}
    </>
  );
}

/** `changes` o `metadata` tal como llegan (ya saneados por la API): sin añadir ni reconstruir claves. */
function RawJson({ value, label }: { value: Record<string, unknown> | null; label: string }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  return (
    <pre aria-label={label} className="max-w-xs overflow-x-auto whitespace-pre-wrap break-words text-xs">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}
